import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import * as addressTools from '@simplybenefits/sb-address';
import { TextInput, PhoneInput, DateInput, Services, FormModal, i18n, tokens, localizationMaps, Loader } from '@simplybenefits/sb-ui-library';
import * as notifier from '../../actions/notifier';
import { updateUserSettings } from '../../actions/user';

class Settings extends Component {
    state = {
        firstname: '',
        lastname: '',
        email: '',

        suite: '',
        firstLine: '',
        city: '',
        province: '',
        postalCode: '',
        country: 'Canada',

        phone: '',
        birthdate: '',
        maritlStat: '',
        gender: '',
        coverageType: '',
        language: ''
    };

    async componentDidMount() {
        const { firstname, lastname, email, coverageType, maritlStat, gender, phone, birthdate } = this.props.meta;
        let mailingAddress = '';

        if (this.props.meta.mailingAddress) {
            mailingAddress = addressTools.parseAddress(this.props.meta.mailingAddress);
        }

        await this.setState({
            firstname: firstname || '',
            lastname: lastname || '',
            email: email || '',

            suite: mailingAddress.suite || '',
            firstLine: mailingAddress.firstLine || '',
            city: mailingAddress.city || '',
            province: localizationMaps.fromEnglish("provinces", mailingAddress.province) || '',
            postalCode: mailingAddress.postalCode || '',

            phone: phone || '',
            birthdate: Services.dates.formatIncomingDate(birthdate) || '',
            maritlStat: localizationMaps.fromEnglish("maritalStatuses", maritlStat) || '',
            gender: localizationMaps.fromEnglish("genders", gender) || '',
            coverageType: localizationMaps.fromEnglish("coverageTypes", coverageType) || '',
            language: this.props.meta.language === "fr" ? "Français" : "English"
        });
    }

    componentDidUpdate(prevProps) {
        if (prevProps.meta.language !== this.props.meta.language) {
            this.setState({ language: this.props.meta.language === "fr" ? "Français" : "English" });
        }
    }

    _onBlur = async (type) => {
        if (type !== 'suite') {
            this.setState({
                [`${ type }Error`]: await Services.validateInput(type, this.state[type])
            });
        }
    };

    _onAddressBlur = async (type) => {
        if (type !== 'suite') {
            await this.setState({
                [`${ type }Error`]: await Services.validateInput(type, this.state[type])
            });
        }

        const { firstLine, city, province, postalCode } = this.state;

        if (firstLine && city && province && postalCode) {
            await addressTools.generateAddress(this.state);
        }

        return null;
    };

    _handleInputChange = (type, event) => {
        this.setState({
            [type]: event.target.value.toString()
        });
    };

    _handleAddressChange = (type, event) => {
        this.setState({
            [type]: event.target.value.replace(/,/g, '')
        });
    };

    _validateForm = async () => {
        const phoneError = await Services.validateInput('phone', this.state.phone);

        const firstLineError = await Services.validateInput('firstLine', this.state.firstLine);
        const cityError = await Services.validateInput('city', this.state.city);
        const provinceError = await Services.validateInput('province', this.state.province);
        const postalCodeError = await Services.validateInput('postalCode', this.state.postalCode);

        const maritlStatError = await Services.validateInput('maritlStat', this.state.maritlStat);
        const genderError = await Services.validateInput('gender', this.state.gender);
        const coverageTypeError = await Services.validateInput('coverageType', this.state.coverageType);

        this.setState({
            phoneError,

            firstLineError,
            cityError,
            provinceError,
            postalCodeError,

            maritlStatError,
            genderError,
            coverageTypeError
        });

        if (
            phoneError ||
            provinceError ||

            firstLineError ||
            cityError ||
            provinceError ||
            postalCodeError ||

            maritlStatError ||
            genderError ||
            coverageTypeError
        ) {
            this.props.dispatch(notifier.displayError({ verbiage: i18n.t(tokens.labels.pleaseConfirmDetailsError) }));

            return false;
        }

        this._confirm();

        return true;
    };

    _confirm = async () => {
        const generatedAddress = addressTools.generateAddress({
            suite: this.state.suite,
            firstLine: this.state.firstLine,
            city: this.state.city,
            province: localizationMaps.toEnglish("provinces", this.state.province),
            postalCode: this.state.postalCode,
            country: this.state.country
        });

        const success = await this.props.dispatch(updateUserSettings(
            {
                firstname: this.state.firstname.trim(),
                lastname: this.state.lastname.trim(),
                email: this.state.email.trim(),
                province: localizationMaps.toEnglish("provinces", this.state.province).trim(),
                mailingAddress: generatedAddress.trim(),
                birthdate: Services.dates.formatOutgoingDate(this.state.birthdate),
                phone: this.state.phone.trim(),
                maritlStat: localizationMaps.toEnglish('maritalStatuses', this.state.maritlStat.trim()),
                gender: localizationMaps.toEnglish("genders", this.state.gender).trim(),
                coverageType: localizationMaps.toEnglish("coverageTypes", this.state.coverageType.trim()),
                language: this.state.language === "Français" ? "fr" : "en",
                // classID must be sent here in order to avoid sending an unnecessary update to Telus
                classID: this.props.meta.classID
            },
            this.props.meta.userID
        ));

        if (success) {
            this.props.close();
        }

        return true;
    };

    render() {
        const {
            birthdate,
            coverageType,
            email,
            firstname,
            lastname,

            suite,
            firstLine,
            city,
            province,
            postalCode,

            phone,
            gender,
            maritlStat,
            language
        } = this.state;

        const styles = {
            form: {
                display: 'flex',
                flexDirection: 'column'
            },
            row: {
                display: 'flex',
                flexDirection: 'row'
            },
            divider: {
                width: 40
            }
        };

        return (
            <FormModal
                testid="settings-modal"
                open={true}
                close={this.props.close}
                title={ i18n.t(tokens.titles["update account details"])}
                disableBackdropClick
                disableEscapeKeyDown
                onSave={this._validateForm}
                customContainerStyle={{ width: 500 }}
            >
                { this.props.loading && <Loader whitelabel={ this.props.whitelabel }/> }

                <TextInput
                    label={ i18n.t(tokens.labels.firstname) }
                    value={firstname}
                    disabled
                    fullWidth
                />

                <TextInput
                    label={ i18n.t(tokens.labels.lastname) }
                    value={lastname}
                    disabled
                    fullWidth
                />

                <TextInput
                    label={ i18n.t(tokens.labels.email) }
                    value={email}
                    disabled
                    fullWidth
                />

                <DateInput
                    label={ i18n.t(tokens.labels.account.dateOfBirthYYYYMMDD)}
                    value={birthdate}
                    disabled
                    fullWidth
                />

                <TextInput
                    label={ i18n.t(tokens.labels.streetAddress) }
                    autocomplete="shipping street-address"
                    value={firstLine}
                    onBlur={() => this._onAddressBlur('firstLine')}
                    onChange={(value) => this._handleAddressChange('firstLine', value)}
                    error={Boolean(this.state.firstLineError)}
                    helperText={this.state.firstLineError}
                    fullWidth
                />

                <div style={styles.row}>
                    <TextInput
                        label={ i18n.t(tokens.labels.account.suiteUnit)}
                        value={suite}
                        onBlur={() => this._onAddressBlur('suite')}
                        onChange={(value) => this._handleAddressChange('suite', value)}
                        fullWidth
                    />

                    <div style={styles.divider} />

                    <TextInput
                        label={ i18n.t(tokens.labels.account.cityTown)}
                        autocomplete="shipping locality"
                        value={city}
                        onBlur={() => this._onAddressBlur('city')}
                        onChange={(value) => this._handleAddressChange('city', value)}
                        error={Boolean(this.state.cityError)}
                        helperText={this.state.cityError}
                        fullWidth
                    />
                </div>


                <div style={styles.row}>
                    <TextInput
                        label={ i18n.t(tokens.labels.province)}
                        autocomplete="shipping region"
                        value={ province }
                        onBlur={() => this._onAddressBlur('province')}
                        onChange={(value) => this._handleInputChange('province', value)}
                        select
                        selectOptions={Services.selectFieldOptions.provinceOptions()}
                        fullWidth
                    />

                    <div style={styles.divider} />

                    <TextInput
                        label={ i18n.t(tokens.labels.postalCode) }
                        autocomplete="shipping postal-code"
                        value={postalCode}
                        onBlur={() => this._onAddressBlur('postalCode')}
                        onChange={(value) => this._handleAddressChange('postalCode', value)}
                        error={Boolean(this.state.postalCodeError)}
                        helperText={this.state.postalCodeError}
                        fullWidth
                    />
                </div>

                <PhoneInput
                    label={ i18n.t(tokens.labels.phoneNumber) }
                    value={phone}
                    onBlur={() => this._onBlur('phone')}
                    onChange={(value) => this._handleInputChange('phone', value)}
                    error={Boolean(this.state.phoneError)}
                    helperText={this.state.phoneError}
                    fullWidth
                />

                <TextInput
                    label={ i18n.t(tokens.labels.maritalStatus) }
                    fullWidth
                    required
                    value={maritlStat}
                    disabled
                />

                <TextInput
                    label={ i18n.t(tokens.labels.gender) }
                    fullWidth
                    required
                    value={ gender }
                    onBlur={() => this._onBlur('gender')}
                    onChange={(value) => this._handleInputChange('gender', value)}
                    error={Boolean(this.state.genderError)}
                    helperText={this.state.genderError}
                    select
                    selectOptions={ Services.selectFieldOptions.genderOptions() }
                />

                <TextInput
                    label={ i18n.t(tokens.labels.coverageType) }
                    value={coverageType}
                    disabled
                    onBlur={() => this._onBlur('coverageType')}
                    onChange={(value) => this._handleInputChange('coverageType', value)}
                    error={Boolean(this.state.coverageTypeError)}
                    helperText={this.state.coverageTypeError}
                    select
                    selectOptions={ Services.selectFieldOptions.coverageOptions() }
                    fullWidth
                />

                <TextInput
                    label={ i18n.t(tokens.labels.signup.language) }
                    value={ language }
                    onBlur={() => this._onBlur('language')}
                    onChange={(value) => this._handleInputChange('language', value)}
                    select
                    selectOptions={ Services.selectFieldOptions.languageOptionsNotTranslated() }
                    fullWidth
                />

            </FormModal>
        );
    }
}

Settings.propTypes = {
    loading: PropTypes.bool,
    meta: PropTypes.object
};

/**
 *
 * @param {*} state
 */
const mapStateToProps = (state) => ({
    loading: state.user.loading,
    whitelabel: state.whitelabel,
    meta: state.user.meta
});

export default connect(mapStateToProps)(Settings);